<template>
    <VJoyDrawer
        :id="dialogId"
        ref="drawer"
        :data-testid="dialogId"
        :data-test-drawer-is-loaded="!loading"
        :size="size"
        :cancel-text="isVisibleCtaCancel ? t('general.cancel') : undefined"
        @drawer:hide="closeDrawer"
        @drawer:confirm="confirm"
    >
        <template #drawer-header><slot name="edit-header" /></template>
        <template #drawer-subheader><slot name="edit-subheader" /></template>

        <template #drawer-body>
            <div class="profile-edit-component__body">
                <section data-testid="edit-component-body">
                    <slot name="edit-body" />
                </section>
                <Transition name="profile-edit-component__transition-loading">
                    <section v-if="loading" class="profile-edit-component__body__loader" data-testid="edit-component-body-loading">
                        <VJoySpinner />
                    </section>
                </Transition>
            </div>
        </template>

        <template #drawer-confirm>
            <VJoyButton
                v-if="isVisibleCtaConfirm"
                :loading="loading"
                :disabled="!confirmEnabled || loading"
                :data-testid="confirmDataTestid"
                variant="main"
                :label="t('general.save')"
                @click="confirm"
            />
        </template>
    </VJoyDrawer>
</template>

<script setup lang="ts">
    import {onMounted, onUnmounted, ref, useTemplateRef, nextTick} from 'vue';
    import {useTranslation} from '#imports';
    import {pushVJoySnackbar, VJoyDrawer, type TJoyDrawerSizes, VJoyButton, VJoySpinner} from '@maltjoy/core-vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import type {Events} from '~/config/events';
    import {eventBus} from '~/config/events';
    import type {EditionOpenedSection} from '~/config/TrackingEvents';
    import {EditionOpenedWorkflow} from '~/config/TrackingEvents';
    import type {TranslationKey} from '#build/i18n';
    import {UnauthorizedResponseHelper} from '@/helpers/unauthorized-response/UnauthorizedResponseHelper';

    type EmitEvents = {
        (e: 'close'): void;
        (e: 'confirm'): void;
    };
    const emit = defineEmits<EmitEvents>();

    defineExpose({
        close,
    });

    function close() {
        drawer.value?.hide();
    }

    const loading = ref(false);

    export type Props = {
        confirmDataTestid?: string;
        confirmEnabled?: boolean;
        dialogId: string;
        openDialogEvent: keyof Events;
        size?: TJoyDrawerSizes;
        successMessageKey?: TranslationKey;
        successHeaderKey?: TranslationKey;
        ignoreDefaultSuccessMessage?: boolean;
        updatedEvent?: string;
        // cta config
        isVisibleCtaConfirm?: boolean;
        isVisibleCtaCancel?: boolean;
        // actions
        confirmFunction?: () => Promise<void | boolean> | void;
        emitCloseFunction?: () => void;
        openDialogPostFunction?: (...args: any[]) => void;
        trackSectionOpened?: EditionOpenedSection;
        trackExistingElement?: boolean;
        scrollToElement?: string;
    };

    const {t} = useTranslation();
    const {trackProfileEdition} = useProfileTracking();
    const props = withDefaults(defineProps<Props>(), {
        confirmEnabled: true,
        size: 'large',
        // cta config
        isVisibleCtaConfirm: true,
        isVisibleCtaCancel: true,
        trackExistingElement: false,
        ignoreDefaultSuccessMessage: false,
    });
    const drawer = useTemplateRef('drawer');

    function closeDrawer() {
        if (props.emitCloseFunction) {
            props.emitCloseFunction();
        }

        emit('close');
    }

    async function confirm() {
        loading.value = true;
        if (props.confirmFunction) {
            try {
                const returnedValue = await props.confirmFunction();
                if (returnedValue === false) {
                    loading.value = false;
                    return;
                }
            } catch (error: unknown) {
                handleExceptionsAndIgnore403(error);
            }
        }
        if (props.updatedEvent) {
            eventBus.emit(props.updatedEvent as keyof Events);
        }

        eventBus.emit('profile:updated');
        emit('confirm');
        if (props.successMessageKey) {
            pushVJoySnackbar({
                props: {
                    message: t(props.successMessageKey),
                    header: props.successHeaderKey ? t(props.successHeaderKey) : undefined,
                    level: 'success',
                },
            });
        } else if (!props.ignoreDefaultSuccessMessage) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.edition.success'),
                    level: 'success',
                },
            });
        }
        drawer.value?.hide();
        loading.value = false;
    }

    async function openDrawer(e: any) {
        drawer.value?.show();

        if (props.openDialogPostFunction) {
            loading.value = true;
            try {
                await props.openDialogPostFunction(e);
            } catch (error: unknown) {
                handleExceptionsAndIgnore403(error);
            }
        }
        loading.value = false;

        if (props.trackSectionOpened && e?.entryPoint) {
            trackProfileEdition(props.trackSectionOpened, e.entryPoint, EditionOpenedWorkflow.MANUAL, props.trackExistingElement);
        }

        await nextTick();
        if (e && e.scrollToElement) {
            const element = document.getElementById(e.scrollToElement);
            if (element) {
                element.scrollIntoView({behavior: 'smooth', block: 'start'});
            }
        }
    }

    function handleExceptionsAndIgnore403(error: any) {
        let message = '';
        let shouldIgnoreThisException = false;

        if (error?.status === 403) {
            message = t('403.forbidden.not.connected');

            if (UnauthorizedResponseHelper.isRejectionCommingFromEdgePlatform(error)) {
                message += '<br/>(Edge platform)';
                shouldIgnoreThisException = true;
            }
        } else {
            message = t('profile.creation.common.notification.error.technical');
        }

        pushVJoySnackbar({props: {message, level: 'error'}});

        if (!shouldIgnoreThisException) {
            throw error;
        }
    }

    onMounted(() => {
        eventBus.on(props.openDialogEvent, openDrawer);
    });

    onUnmounted(() => {
        eventBus.off(props.openDialogEvent, openDrawer);
    });
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .profile-edit-component {
        &__header {
            @include text.getFontBySize(primary-850);

            color: var(--joy-color-neutral-70);
        }

        &__body {
            width: 100%;
            height: 100%;

            section {
                width: 100%;
                height: 100%;
            }

            &__loader {
                display: flex;
                justify-content: center;
                align-items: center;
                position: absolute;
                top: 0;
                z-index: 999;
                background-color: rgb(255 255 255 / 60%);
            }
        }

        &__transition-loading {
            &-enter-active,
            &-leave-active {
                transition: opacity var(--joy-transition-duration-long) var(--joy-transition-timing-function);
            }

            &-enter-from,
            &-leave-to {
                opacity: 0;
            }
        }
    }
</style>
